import { Injectable, inject, signal } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import type { CreateInvestmentRequest } from '@core/api/contracts';
import { InvestmentApiClient } from '@features/investment/services/investment-api.client';
import { InvestmentFeedbackService } from '@features/investment/services/investment-feedback.service';
import { LoadingService } from '@shared/utils/loading.service';
import { PageHeaderConfig } from '@shared/components/layout/page-header/page-header.models';
import {
  FormWizardConfig,
  SelectOption,
} from '@shared/components/form/form-wizard/models/form-wizard.models';
import {
  INVESTMENT_FORM_SECTION_IDS,
  InvestmentForm,
  getInvestmentFormConfigForCreate
} from '@features/investment/config/investment-form.config';
import {
  InvestmentLocationData,
  toInvestmentAddressRequest
} from '@features/investment/utils/investment-location.utils';
import { InvestmentCompanyBindingService } from '@features/investment/services/investment-company-binding.service';
import { resolveOptionalCompanyId } from '@features/investment/utils/investment-company.utils';
import { extractEntityId } from '@features/investment/utils/investment-form.utils';
import { normalizeDateOnly } from '@shared/utils/date-only.util';

type CreateInvestmentBuildingRequest = NonNullable<CreateInvestmentRequest['buildings']>[number];
type CreateInvestmentBuildingAddressRequest = CreateInvestmentBuildingRequest['addresses'][number];

@Injectable()
export class InvestmentCreateFacade {
  private readonly router = inject(Router);
  private readonly investmentApi = inject(InvestmentApiClient);
  private readonly companyBindingService = inject(InvestmentCompanyBindingService);
  private readonly feedback = inject(InvestmentFeedbackService);
  private readonly loadingService = inject(LoadingService);

  readonly isLoading = signal(false);
  readonly locationData = signal<InvestmentLocationData | null>(null);
  readonly locationModalVisible = signal(false);
  readonly isSavingLocationModal = signal(false);
  private readonly locationModalSaveClosePending = signal(false);
  readonly headerConfig = signal<PageHeaderConfig>({
    title: 'Nowa Inwestycja',
    subtitle: 'Dodajesz nową inwestycję do portalu',
    icon: 'pi-building',
    backRoute: '/home/investments'
  });

  readonly companyOptions = signal<SelectOption[]>([]);
  readonly formConfig = signal<FormWizardConfig<InvestmentForm>>(this.createFormConfig());
  private createForm: FormGroup | null = null;
  private preselectedMainCompanyId: number | null = null;

  readonly initialData = signal<Record<string, unknown>>({
    saleStartDate: new Date(),
    companyId: null,
    buildingsToCreate: [],
    locationDisplay: '',
    openLocationModal: null
  });

  init(): void {
    void this.loadCompanyContext();
  }

  bindForm(form: FormGroup | null | undefined): void {
    this.createForm = form ?? null;
    this.tryApplyMainCompanyPreselect();
  }

  openLocationModal(): void {
    this.locationModalVisible.set(true);
  }

  setLocationModalVisible(visible: boolean): void {
    if (!visible && this.isSavingLocationModal()) {
      return;
    }

    this.locationModalVisible.set(visible);
  }

  onLocationModalHidden(): void {
    if (!this.locationModalSaveClosePending()) {
      return;
    }

    this.locationModalSaveClosePending.set(false);
    this.isSavingLocationModal.set(false);
  }

  async submit(formData: InvestmentForm): Promise<void> {
    if (this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.loadingService.show();

    try {
      const createCommand = this.transformToCreateCommand(formData);
      const result = await firstValueFrom(this.investmentApi.createInvestment(createCommand));
      const investmentId = extractEntityId(result);
      const investmentTitle = formData.title || 'Nowa inwestycja';

      this.feedback.success(`Inwestycja "${investmentTitle}" została utworzona pomyślnie`);

      if (investmentId) {
        await this.router.navigateByUrl(`/home/investments/${investmentId}/view?onboarding=true`);
      } else {
        await this.router.navigateByUrl('/home/investments');
      }
    } catch (error) {
      this.feedback.error(error, 'Wystąpił błąd podczas tworzenia inwestycji');
      throw error;
    } finally {
      this.isLoading.set(false);
      this.loadingService.hide();
    }
  }

  cancel(): void {
    void this.router.navigate(['/home/investments']);
  }

  private createFormConfig(): FormWizardConfig<InvestmentForm> {
    const baseConfig = getInvestmentFormConfigForCreate();
    const sections = baseConfig.sections?.map((section) => {
      if (section.id !== INVESTMENT_FORM_SECTION_IDS.CREATE_BASIC) {
        return section;
      }

      return {
        ...section,
        fields: section.fields.map((field) => {
          if (field.key !== 'openLocationModal') {
            if (field.key !== 'companyId') {
              return field;
            }

            return {
              ...field,
              dynamicOptions: () => this.companyOptions(),
            };
          }

          return {
            ...field,
            action: () => this.openLocationModal(),
            buttonLabel: 'Ustaw / edytuj adres inwestycji',
            severity: 'primary' as const,
            size: 'large' as const,
            topContent: undefined,
            colSpan: 12 as const
          };
        })
      };
    });

    return {
      ...baseConfig,
      sections,
      onSubmit: (data: InvestmentForm) => this.submit(data)
    };
  }

  private patchLocationFormControls(form: FormGroup | null | undefined, address?: string): void {
    if (!form || !address) {
      return;
    }

    form.patchValue({
      locationDisplay: address,
      openLocationModal: address
    });

    form.get('locationDisplay')?.markAsDirty();
    form.get('openLocationModal')?.markAsDirty();
  }

  async saveLocation(data: InvestmentLocationData): Promise<void> {
    this.isSavingLocationModal.set(true);
    this.locationData.set(data);
    this.patchLocationFormControls(this.createForm, data.displayAddress);
    this.locationModalSaveClosePending.set(true);
    this.locationModalVisible.set(false);
  }

  private transformToCreateCommand(formData: InvestmentForm): CreateInvestmentRequest {
    const title = typeof formData.title === 'string' ? formData.title.trim() : '';
    if (!title) {
      throw new Error('Nazwa inwestycji jest wymagana');
    }

    const saleStartDate = normalizeDateOnly(formData.saleStartDate);
    if (!saleStartDate) {
      throw new Error('Data rozpoczęcia sprzedaży jest wymagana');
    }

    const buildings = this.resolveCreateBuildings(formData);
    const companyId = resolveOptionalCompanyId(formData.companyId);

    return {
      title,
      location: this.getLocationRequest(),
      saleStartDate,
      internalNote: formData.internalNote,
      ...(companyId ? { companyId } : {}),
      ...(buildings ? { buildings } : {})
    };
  }

  private getLocationRequest(): CreateInvestmentRequest['location'] {
    const data = this.locationData();
    if (!data) {
      throw new Error('Adres inwestycji jest wymagany');
    }

    return toInvestmentAddressRequest(data);
  }

  private async loadCompanyContext(): Promise<void> {
    try {
      const [options, mainCompanyId] = await Promise.all([
        this.companyBindingService.loadCompanyFormOptions(),
        this.companyBindingService.loadDeveloperMainCompanyId().catch(() => null),
      ]);

      this.companyOptions.set(options);
      this.preselectedMainCompanyId = options.some((option) => option.value === mainCompanyId)
        ? mainCompanyId
        : null;
      this.tryApplyMainCompanyPreselect();
    } catch (error) {
      this.companyOptions.set([]);
      this.preselectedMainCompanyId = null;
      this.feedback.error(error, 'Nie udało się załadować listy firm. Inwestycja zostanie utworzona bez przypisanej firmy.');
    }
  }

  private tryApplyMainCompanyPreselect(): void {
    const form = this.createForm;
    if (!form) {
      return;
    }

    const companyControl = form.get('companyId');
    if (!companyControl) {
      return;
    }

    const selectedCompanyId = resolveOptionalCompanyId(companyControl.value);
    if (selectedCompanyId) {
      return;
    }

    if (companyControl.dirty || companyControl.touched) {
      return;
    }

    const candidate = this.preselectedMainCompanyId;
    if (!candidate) {
      return;
    }

    const existsInOptions = this.companyOptions().some((option) => option.value === candidate);
    if (!existsInOptions) {
      return;
    }

    companyControl.setValue(candidate, { emitEvent: false });
    companyControl.markAsPristine();
    companyControl.markAsUntouched();
  }

  private resolveCreateBuildings(
    formData: InvestmentForm
  ): CreateInvestmentBuildingRequest[] | undefined {
    const source = formData.buildingsToCreate ?? formData.buildings;
    if (!Array.isArray(source)) {
      return undefined;
    }

    const normalized = source
      .map((item) => this.toCreateBuildingRequest(item))
      .filter((item): item is CreateInvestmentBuildingRequest => item !== null);

    if (normalized.length === 0) {
      return undefined;
    }

    return normalized;
  }

  private toCreateBuildingRequest(value: unknown): CreateInvestmentBuildingRequest | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const candidate = value as {
      buildingCode?: unknown;
      addresses?: unknown;
    };

    if (typeof candidate.buildingCode !== 'string' || !candidate.buildingCode.trim()) {
      return null;
    }

    if (!Array.isArray(candidate.addresses)) {
      return null;
    }

    const addresses = candidate.addresses
      .map((address) => this.toBuildingAddress(address))
      .filter((address): address is CreateInvestmentBuildingAddressRequest => address !== null);

    if (addresses.length === 0) {
      return null;
    }

    return {
      buildingCode: candidate.buildingCode.trim(),
      addresses
    };
  }

  private toBuildingAddress(value: unknown): CreateInvestmentBuildingAddressRequest | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const candidate = value as { buildingNumber?: unknown; entranceCode?: unknown };
    const buildingNumber = typeof candidate.buildingNumber === 'string' && candidate.buildingNumber.trim().length > 0
      ? candidate.buildingNumber.trim()
      : undefined;
    const entranceCode = typeof candidate.entranceCode === 'string' && candidate.entranceCode.trim().length > 0
      ? candidate.entranceCode.trim()
      : undefined;

    return {
      ...(buildingNumber ? { buildingNumber } : {}),
      ...(entranceCode ? { entranceCode } : {})
    };
  }

}
